import { DictContext } from "@/layout/context/DictContext";
import { DictEnum, DictItem } from "@/types/dict";
import dictApi from "@/apis/dict";
import { App, Button, Divider, Input, notification, Select, SelectProps } from "antd";
import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

type SelectProductTypeProps<T> = Omit<SelectProps<T>, "options">

function SelectProductType<T>(props: SelectProductTypeProps<T>) {
  const { t } = useTranslation();
  const { productType, setProductType } = useContext(DictContext);
  const [isShowAdd, setIsShowAdd] = useState<boolean>(false);
  const [addVal, setAddVal] = useState<string>("");
  const [adding, setAdding] = useState<boolean>(false);

  const closeAdd = () => {
    setIsShowAdd(false);
    setAddVal("");
  };

  const handleAdd = () => {
    const desc = addVal.trim();
    if (!desc || productType.some(item => item.desc === desc)) {
      closeAdd();
      return;
    }
    setAdding(true);
    dictApi.add(DictEnum.PRODUCT_TYPE, { key: desc, desc })
      .then((res: DictItem) => {
        setProductType([...productType, res]);
        closeAdd();
      })
      .catch((err) => {
        notification.error({
          placement: 'top',
          message: t("common.addError"),
          description: err === 10010 ? t("common.networkError") : t("common.serverError"),
        });
      })
      .finally(() => setAdding(false));
  };

  return (
    <Select
      {...props}
      showSearch
      options={
        productType.map(item => ({ key: item.id, label: item.desc, value: item.desc }))
      }
      filterOption={(input, option) =>
        String(option?.label ?? "").toLowerCase().includes(String(input).toLowerCase())
      }
      onDropdownVisibleChange={(open) => {
        if (!open) closeAdd();
        props.onDropdownVisibleChange?.(open);
      }}
      placeholder={t('purchase.productType')}
      dropdownRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: "8px 0" }} />
          {isShowAdd ? (
            <div style={{ display: "flex", gap: 4, padding: "0 4px" }}>
              <Input
                autoFocus
                size="small"
                value={addVal}
                onChange={(e) => setAddVal(e.target.value)}
                onPressEnter={handleAdd}
                placeholder={t('purchase.productType')}
                maxLength={100}
              />
              <Button type="link" size="small" icon={<CheckOutlined />} loading={adding} onClick={handleAdd} />
              <Button type="link" size="small" danger icon={<CloseOutlined />} onClick={closeAdd} />
            </div>
          ) : (
            <Button
              type="text"
              size="small"
              style={{ width: "100%", color: "#1677ff" }}
              icon={<PlusOutlined />}
              onClick={() => setIsShowAdd(true)}
            >
              {t("common.add")}
            </Button>
          )}
        </>
      )}
    />
  )
};

export default SelectProductType;
